// src/context/LimitExceededContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { setLimitExceededCallback as setImageApiCallback } from '../services/imageGenerationApi';
import { setLimitExceededCallback as setCentralCallback } from '../services/apiErrorHandler';

interface LimitExceededContextType {
  isModalOpen: boolean;
  modalMessage: string;
  showLimitModal: (message?: string) => void;
  closeLimitModal: () => void;
}

const LimitExceededContext = createContext<LimitExceededContextType | undefined>(undefined);

interface LimitExceededProviderProps {
  children: ReactNode;
}

export const LimitExceededProvider: React.FC<LimitExceededProviderProps> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('Limit exceeded. Upgrade your plan or purchase credits.');

  const showLimitModal = (message?: string) => {
    if (message) {
      setModalMessage(message);
    }
    setIsModalOpen(true);
  };

  const closeLimitModal = () => {
    setIsModalOpen(false);
    // Reset to default message after close
    setTimeout(() => {
      setModalMessage('Limit exceeded. Upgrade your plan or purchase credits.');
    }, 300);
  };

  // Register callback with service layer on mount
  useEffect(() => {
    // Register with both the centralized handler and the image API
    setCentralCallback(showLimitModal);
    setImageApiCallback(showLimitModal);
    
    // Cleanup on unmount
    return () => {
      setCentralCallback(() => {});
      setImageApiCallback(() => {});
    };
  }, []);

  return (
    <LimitExceededContext.Provider
      value={{
        isModalOpen,
        modalMessage,
        showLimitModal,
        closeLimitModal,
      }}
    >
      {children}
    </LimitExceededContext.Provider>
  );
};

export const useLimitExceeded = (): LimitExceededContextType => {
  const context = useContext(LimitExceededContext);
  if (!context) {
    throw new Error('useLimitExceeded must be used within a LimitExceededProvider');
  }
  return context;
};
